<template>
  <form @submit.prevent="handleSubmit">
    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="name" class="validate" required />
        <label class="active teal-text" for="name">Principal Name</label>
      </div>
    </div>

    <div class="card-panel">
      <div class="row">
        <div class="col s10">
          <div class="file-field input-field">
            <div class="btn grey">
              <span>Change</span>
              <input type="file" @change="handleFile" />
            </div>
            <div class="file-path-wrapper">
              <input class="file-path" v-model="inputFile" type="text" />
              <p v-if="fileError">{{ fileError }}</p>
            </div>
          </div>
        </div>
        <div class="col s2">
          <LoadingSmallDiv v-if="isLoading" />
          <span v-else>
            <img :src="img" width="64" class="circle responsive-img" v-if="img" />
            <span v-else>
              <img :src="enrollee.profile_img" width="64" class="circle responsive-img" v-if="enrollee.profile_img" />
              <img :src="`https://ui-avatars.com/api/?name=${enrollee.name}&size=128`" width="64"
                class="circle responsive-img" v-else />
            </span>
          </span>
        </div>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <label class="active teal-text" for="sex">Sex</label>
        <select id="sex" v-model="sex" class="validate" required>
          <option value="m">Male</option>
          <option value="f">Female</option>
        </select>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="dob" class="datepicker validate" required />
        <label class="active teal-text" for="dob">Date of Birth</label>
      </div>
      <div class="age-policy-note" v-if="ageError">
        <i class="material-icons">policy</i>
        <span>{{ ageError }}</span>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="email" v-model="email" class="validate" v-if="allowEmailChange" />
        <input type="email" v-model="email" disabled v-else />
        <label class="active teal-text" for="email">Email</label>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="phone" class="validate" required />
        <label class="active teal-text" for="phone">Phone</label>
      </div>
    </div>

    <div class="card-panel">
      <div class="input-field">
        <input type="text" v-model="address" class="validate" required />
        <label class="active teal-text" for="address">Address</label>
      </div>
    </div>

    <div class="card-content">
      <p v-if="uploadError"><br />{{ uploadError }}</p>

      <p>
        <br />
        <a
          href="#"
          class="text-back-action"
          :class="{
            disabled: isSubmitting,
          }"
          @click.prevent="handleBack"
        >
          <i class="material-icons">arrow_back</i>Back
        </a>
        <button class="waves-effect waves-light btn-large" :class="{
          disabled: isSubmitting || ageError,
        }">
          <i class="material-icons right">check_circle</i>{{ isSubmitting ? "Updating" : "Update" }}
        </button>
      </p>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, onUpdated, ref, watchEffect } from "vue";
import putItem from "@/composables/putItem";
import useStorage from "@/composables/useStorage";
import LoadingSmallDiv from "@/components/LoadingSmallDiv.vue";
import M from "materialize-css";

export default {
  name: "EnrolleeEditOnboardComponent",
  components: { LoadingSmallDiv },
  props: ["enrollee", "policy", "serverDate"],
  emits: ["reset"],
  setup(props, context) {
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const name = ref(null);
    const sex = ref(null);
    const dob = ref(null);
    const email = ref(null);
    const allowEmailChange = ref(null);
    const phone = ref(null);
    const address = ref(null);
    const isSubmitting = ref(false);
    const isLoading = ref(false);
    const error = ref(null);
    const img = ref(null);
    const originalDob = ref(null);
    const types = ["image/jpg", "image/jpeg", "image/png", "image/gif"];
    const parseLocalDate = (value) => {
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-").map(Number);
        return new Date(year, month - 1, day);
      }

      return new Date(value);
    };
    const referenceDate = computed(() => {
      const date = props.serverDate ? parseLocalDate(props.serverDate) : new Date();
      return isNaN(date) ? new Date() : date;
    });
    const principalAgeLimit = computed(() => Number(props.policy?.principal_age) || 0);
    const memberAge = computed(() => {
      if (!dob.value) return null;

      const date = parseLocalDate(dob.value);
      if (isNaN(date)) return null;

      const today = referenceDate.value;
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age -= 1;
      }

      return age;
    });
    const ageError = computed(() => {
      if (!principalAgeLimit.value || memberAge.value === null) return null;
      if (memberAge.value > principalAgeLimit.value && (!originalAge.value || memberAge.value > originalAge.value)) {
        return `Principal age exceeds the current policy limit of ${principalAgeLimit.value} years.`;
      }
      return null;
    });
    const originalAge = computed(() => calculateAge(originalDob.value));

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // loader
    onMounted(() => {
      name.value = props.enrollee.name;
      sex.value = props.enrollee.sex;
      originalDob.value = props.enrollee.dob;

      dob.value = new Date(props.enrollee.dob).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      email.value = props.enrollee.email || props.enrollee.user?.email;
      allowEmailChange.value = props.enrollee.user_created !== "y";

      phone.value = props.enrollee.phone;
      address.value = props.enrollee.address;
    });

    // select
    onMounted(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});

      // activate date
      const elemsDate = document.querySelectorAll(".datepicker");
      const startDate = '1980-01-01';
      const defaultDate = dob.value ? new Date(dob.value) : new Date(startDate);

      M.Datepicker.init(elemsDate, {
        onSelect: function (date) {
          dob.value = date;
        },
        yearRange: [new Date().getFullYear() - 100, new Date().getFullYear()],
        defaultDate: defaultDate,
        setDefaultDate: !!defaultDate, // only true if defaultDate is set
      });
    });

    // select
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    });

    // clear
    const clearForm = () => {
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      isSubmitting.value = false;
      isLoading.value = false;
      uploadProgress.value = "0%";
    };

    // handle file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Image (.JPG, .PNG, .GIF)";
      }
    };

    const handleData = () => {
      // console.log("data");
    };

    // handle back
    const handleBack = () => {
      context.emit("reset", null);
    };

    const calculateAge = (value) => {
      if (!value) return null;

      const date = parseLocalDate(value);
      if (isNaN(date)) return null;

      const today = referenceDate.value;
      let age = today.getFullYear() - date.getFullYear();
      const monthDiff = today.getMonth() - date.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
        age -= 1;
      }

      return age;
    };

    //hand
    watchEffect(() => {
      if (file.value) {
        // start ploading
        isLoading.value = true;
        uploadError.value = null;

        // upload image
        uploadFile(file.value);
      }
    });

    //handle submit
    const handleSubmit = async () => {
      // Materialize CSS handled all custome errors \\
      if (ageError.value) {
        error.value = ageError.value;
        return;
      }

      // start
      isSubmitting.value = true;
      error.value = false;
      uploadProgress.value = false;

      // re-format date
      let newDate = new Date(dob.value);
      newDate = newDate
        .toLocaleDateString("en-GB")
        .split("/")
        .reverse()
        .join("-");

      // variable
      const newVal = {
        name: name.value,
        sex: sex.value,
        dob: newDate,
        phone: phone.value,
        address: address.value,
      };
      if (allowEmailChange.value) {
        newVal.email = email.value || null;
      }
      // check new image
      if (img.value) {
        newVal.profile_img = img.value;
      }

      // post item
      const { res, err } = await putItem(
        "principal",
        props.enrollee.id,
        newVal
      );

      if (res.value) {
        // start
        let principal;
        // next
        principal = {
          ...props.enrollee,
          ...res.value.data,
        };

        // go back
        context.emit("reset", principal);
      } else {
        error.value = err.value;
      }

      // end here
      // clear
      clearForm();
    };

    // watch tariff upload
    watchEffect(() => {
      if (completed.value) {
        // set image
        img.value = data.value;
        // clear
        clearForm();
        // data.succ.Success, data.succ.inserted_rows, data.succ.Error
        // console.log(data); // might not be necessary
      }
      if (uploadError.value) {
        // clear
        clearForm();
      }
    });

    return {
      handleFile,
      handleData,
      handleBack,
      handleSubmit,
      isSubmitting,
      isLoading,
      error,
      uploadProgress,
      fileError,
      uploadError,
      file,
      inputFile,
      img,
      name,
      sex,
      dob,
      ageError,
      email,
      allowEmailChange,
      phone,
      address,
    };
  },
};
</script>

<style scoped>
.text-back-action {
  align-items: center;
  color: #455a64;
  display: inline-flex;
  gap: 5px;
  font-weight: 600;
  margin-right: 14px;
  padding: 12px 0;
}

.text-back-action i {
  font-size: 18px;
}

.text-back-action.disabled {
  opacity: 0.55;
  pointer-events: none;
}

.age-policy-note {
  align-items: center;
  background: #fff3e0;
  border-left: 4px solid #ef6c00;
  border-radius: 4px;
  color: #4e342e;
  display: flex;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
}

.age-policy-note i {
  color: #ef6c00;
  font-size: 20px;
}
</style>
